import { getBudgetAmountAt, getBudgetAmountInRange } from './budgetAmounts';
/**
 * Per-account balance projection: live balance + the rhythm + the events
 * (docs/ACTION-SCHEDULING-DESIGN.md, "Balance forecast per account").
 *
 * - Budgets contribute the baseline as a prorated daily flow — income budgets
 *   up, expense budgets down. The current period's flow is the budget amount
 *   minus what's already spent (the baseline self-corrects), and in every
 *   period committed action occurrences in the same category reduce the flow
 *   up to the budget's cap, so a budgeted category with a planned action is
 *   never counted twice ("never budget + action").
 * - Actions land as dated steps: income up, expense down, transfers moving
 *   money from source to destination account.
 * - An "already cleared?" check near today keeps the chart honest: an
 *   occurrence due near today that matches a real transaction is already
 *   inside the live balance and is skipped; an overdue occurrence with no
 *   matching transaction stays in the projection and lands tomorrow.
 */

import { generateOccurrences } from './generateOccurrences';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// "Already cleared?" matcher: days either side of an occurrence's due date a
// clearing transaction may sit, and how far the amount may drift (fraction).
export const CLEARED_DATE_TOLERANCE = 4;
export const CLEARED_AMOUNT_TOLERANCE = 0.1;

// Safe-to-spend and the budget-overlap rule look past the chart horizon so a
// short chart window doesn't change their answers.
const OCCURRENCE_LOOKAHEAD_DAYS = 90;

const toLocalDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

// Whole days from b to a (both local midnight); rounding absorbs DST hours.
const dayDiff = (a, b) => Math.round((a - b) / MS_PER_DAY);

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Project every active account's balance forward.
 *
 * @param {Object} args
 * @param {Array} args.accounts - accounts with live `balance`
 * @param {Array} args.actions - scheduled actions
 * @param {Array} args.budgets - budgets (the prorated rhythm)
 * @param {Array} args.transactions - real transactions, for the "already
 *   cleared?" check and current-period spent
 * @param {Date} [args.today]
 * @param {number} [args.days=30] - chart horizon
 * @returns {{
 *   start: Date, days: number, accounts: Array,
 *   rows: Array<{date: Date, ts: number, total: number}>,  // + [accountId]: balance
 *   events: Array<{date: Date, accountId: string, amount: number, name: string, type: string}>,
 *   minima: Object<string, {balance: number, date: Date, crossesZeroOn: Date|null}>,
 *   safeToSpend: {amount: number, balance: number, committed: number,
 *                 untilDate: Date, nextIncomeName: string|null}|null,
 *   nextIncome: {date: Date, name: string}|null,
 * }}
 */
export const projectBalances = ({
  accounts = [],
  actions = [],
  budgets = [],
  transactions = [],
  today = new Date(),
  days = 30,
} = {}) => {
  const start = toLocalDay(today);
  const horizonEnd = addDays(start, days);
  const wideEnd = addDays(start, Math.max(days, OCCURRENCE_LOOKAHEAD_DAYS));

  const included = accounts.filter((a) => a && a.id && a.isActive !== false);
  // Per-account signed deltas for each projected day; index 0 is today and
  // stays empty — the series is anchored to the live balance.
  const deltas = {};
  included.forEach((a) => { deltas[a.id] = new Array(days + 1).fill(0); });

  // ---------------------------------------------------------------- actions

  // Transactions near today, for the cleared check. Each real transaction may
  // clear at most one occurrence.
  const matchWindowStart = addDays(start, -CLEARED_DATE_TOLERANCE * 2);
  const recentTxns = transactions.filter((t) => {
    const d = toLocalDay(t.date);
    return d && d >= matchWindowStart && d <= start;
  });
  const consumed = new Set();

  const findClearingTxn = (accountId, signedAmount, dueDate) => {
    const target = Math.abs(signedAmount);
    return recentTxns.find((t) => {
      const key = t._id || t.id;
      if (key && consumed.has(key)) return false;
      if (t.account !== accountId) return false;
      if (signedAmount < 0 ? t.amount >= 0 : t.amount <= 0) return false;
      if (Math.abs(Math.abs(t.amount) - target) > target * CLEARED_AMOUNT_TOLERANCE + 0.005) return false;
      const d = toLocalDay(t.date);
      return d && Math.abs(dayDiff(d, dueDate)) <= CLEARED_DATE_TOLERANCE;
    });
  };

  // Every surviving occurrence, cleared-checked and (for overdue ones)
  // redated. Generated over the wide window so safe-to-spend and the budget
  // overlap rule see past the chart horizon.
  const occurrences = [];
  actions
    .filter((a) => a && a.isActive !== false && !a.isCompleted)
    .forEach((action) => {
      const amt = Math.abs(Number(action.amount) || 0);
      if (!amt) return;
      const legs = action.type === 'transfer'
        ? [
            { accountId: action.sourceAccountId, amount: -amt },
            { accountId: action.destinationAccountId, amount: amt },
          ]
        : [{ accountId: action.sourceAccountId, amount: action.type === 'income' ? amt : -amt }];

      generateOccurrences(action, addDays(start, -CLEARED_DATE_TOLERANCE), wideEnd)
        .forEach((due) => {
          if (Math.abs(dayDiff(due, start)) <= CLEARED_DATE_TOLERANCE) {
            // Near today: if a real transaction matches (account, direction,
            // amount within tolerance, date within tolerance), the occurrence
            // already happened — the live balance includes it, so projecting
            // it again would double-count. Transfers match on the source leg.
            const txn = findClearingTxn(legs[0].accountId, legs[0].amount, due);
            if (txn) {
              consumed.add(txn._id || txn.id);
              return;
            }
          }
          // Overdue (or due today) and not cleared: still coming. Land it
          // tomorrow — today's point stays anchored to the live balance.
          const date = due <= start ? addDays(start, 1) : due;
          occurrences.push({ action, date, legs });
        });
    });

  const events = [];
  occurrences.forEach(({ action, date, legs }) => {
    const idx = dayDiff(date, start);
    if (idx < 1 || idx > days) return;
    legs.forEach(({ accountId, amount }) => {
      if (!deltas[accountId]) return;
      deltas[accountId][idx] += amount;
      events.push({ date, accountId, amount, name: action.name, type: action.type, actionId: action.id });
    });
  });

  // ---------------------------------------------------------------- budgets

  // Committed action amounts in a category over a date span — the overlap
  // rule's input. Transfers and uncategorized actions sit outside budget math.
  const committedIn = (categoryId, actionType, from, to) => {
    if (!categoryId) return 0;
    return occurrences.reduce((sum, occ) => {
      if (occ.action.categoryId !== categoryId || occ.action.type !== actionType) return sum;
      if (occ.date < from || occ.date > to) return sum;
      return sum + Math.abs(Number(occ.action.amount) || 0);
    }, 0);
  };

  const spentInPeriod = (budget, from, to) => {
    const wantPositive = budget.type === 'Income';
    return transactions.reduce((sum, t) => {
      if (t.category !== budget.categoryId) return sum;
      if (wantPositive ? t.amount <= 0 : t.amount >= 0) return sum;
      const d = toLocalDay(t.date);
      if (!d || d < from || d > to) return sum;
      return sum + Math.abs(t.amount);
    }, 0);
  };

  // A budget without an account ("All Accounts") flows through the primary
  // checking account — the account day-to-day rhythm actually moves through.
  const primaryChecking = included
    .filter((a) => a.type === 'checking')
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))[0] || included[0];

  const activeBudgets = budgets.filter(
    (b) => b && b.isActive !== false && (b.type === 'Income' || b.type === 'Expense')
  );

  activeBudgets.forEach((budget) => {
    const account =
      (budget.accountId && included.find((a) => a.id === budget.accountId)) || primaryChecking;
    if (!account) return;
    const sign = budget.type === 'Income' ? 1 : -1;
    const actionType = budget.type === 'Income' ? 'income' : 'expense';
    const amount = Math.abs(Number(budget.amount) || 0);
    if (!amount && !budget.amountHistory?.length) return;

    // Period boundaries anchored on the budget's own schedule: each period
    // runs from one occurrence date to the day before the next.
    const periods = [];
    if (budget.isRecurring) {
      const anchors = generateOccurrences(
        { ...budget, endDate: undefined },
        addDays(start, -370),
        addDays(horizonEnd, 400) // guarantees an anchor past the horizon at any cadence
      );
      const hardEnd = toLocalDay(budget.endDate);
      for (let i = 0; i < anchors.length - 1; i++) {
        const from = anchors[i];
        if (hardEnd && from > hardEnd) break;
        let to = addDays(anchors[i + 1], -1);
        if (hardEnd && to > hardEnd) to = hardEnd;
        periods.push({ from, to });
      }
    } else {
      const from = toLocalDay(budget.startDate);
      const to = toLocalDay(budget.endDate);
      if (from && to && to >= from) {
        periods.push({ from, to });
      } else if (from && from > start) {
        // No end date to prorate across: the figure lands whole on its date.
        const idx = dayDiff(from, start);
        if (idx <= days) {
          deltas[account.id][idx] += sign * amount;
          events.push({ date: from, accountId: account.id, amount: sign * amount, name: budget.name || 'Budget', type: 'budget' });
        }
        return;
      } else {
        return; // started, open-ended, non-recurring: no rate to derive
      }
    }

    periods.forEach(({ from, to }) => {
      const periodAmount = budget.amountHistory?.length
        ? (budget.isProrated ? getBudgetAmountInRange(budget, from, to) : Math.abs(getBudgetAmountAt(budget, from)))
        : amount;
      if (to <= start || from > horizonEnd) return;
      const committed = committedIn(
        budget.categoryId,
        actionType,
        from > start ? from : addDays(start, 1),
        to
      );

      let rate;
      let flowFrom;
      if (from <= start) {
        // Current period: only what's left of the figure keeps flowing,
        // spread over the days that remain.
        const spent = spentInPeriod(budget, from, start);
        const remainingDays = dayDiff(to, start);
        if (remainingDays <= 0) return;
        rate = Math.max(0, periodAmount - spent - committed) / remainingDays;
        flowFrom = addDays(start, 1);
      } else {
        rate = Math.max(0, periodAmount - committed) / (dayDiff(to, from) + 1);
        flowFrom = from;
      }
      const first = Math.max(1, dayDiff(flowFrom, start));
      const last = Math.min(days, dayDiff(to, start));
      for (let i = first; i <= last; i++) deltas[account.id][i] += sign * rate;
    });
  });

  // ----------------------------------------------------------------- series

  const rows = [];
  const running = {};
  included.forEach((a) => { running[a.id] = Number(a.balance) || 0; });
  for (let i = 0; i <= days; i++) {
    const date = addDays(start, i);
    const row = { date, ts: date.getTime() };
    let total = 0;
    included.forEach((a) => {
      running[a.id] += deltas[a.id][i];
      row[a.id] = round2(running[a.id]);
      total += running[a.id];
    });
    row.total = round2(total);
    rows.push(row);
  }

  const minima = {};
  included.forEach((a) => {
    let min = Infinity;
    let minDate = start;
    let crossesZeroOn = null;
    rows.forEach((row, i) => {
      const v = row[a.id];
      if (v < min) { min = v; minDate = row.date; }
      if (crossesZeroOn === null && i > 0 && v < 0 && rows[i - 1][a.id] >= 0) {
        crossesZeroOn = row.date;
      }
    });
    minima[a.id] = { balance: min, date: minDate, crossesZeroOn };
  });

  // ---------------------------------------------------------- safe to spend

  // Next income occurrence: income actions, plus income budgets' anchor dates
  // (the rhythm knows when pay lands even when no income action exists).
  const incomeDates = [];
  occurrences.forEach((o) => {
    if (o.action.type === 'income' && o.date > start) {
      incomeDates.push({ date: o.date, name: o.action.name });
    }
  });
  activeBudgets
    .filter((b) => b.type === 'Income' && b.isRecurring)
    .forEach((b) => {
      generateOccurrences(b, addDays(start, 1), wideEnd).forEach((d) => {
        incomeDates.push({ date: d, name: b.name || null });
      });
    });
  incomeDates.sort((a, b) => a.date - b.date);
  const nextIncome = incomeDates[0] || null;

  // Safe to spend = checking balance − committed action outflows strictly
  // before the next income lands. Transfers count only when they move money
  // in or out of checking (checking → checking nets to zero).
  const checking = included.filter((a) => a.type === 'checking');
  let safeToSpend = null;
  if (checking.length) {
    const checkingIds = new Set(checking.map((a) => a.id));
    const balance = checking.reduce((s, a) => s + (Number(a.balance) || 0), 0);
    const untilDate = nextIncome ? nextIncome.date : addDays(start, 30);
    let committed = 0;
    occurrences.forEach(({ action, date, legs }) => {
      if (action.type === 'income') return;
      if (date <= start || date >= untilDate) return;
      legs.forEach(({ accountId, amount }) => {
        if (checkingIds.has(accountId)) committed -= amount;
      });
    });
    safeToSpend = {
      amount: round2(balance - committed),
      balance: round2(balance),
      committed: round2(committed),
      untilDate,
      nextIncomeName: nextIncome ? nextIncome.name : null,
    };
  }

  return { start, days, accounts: included, rows, events, minima, safeToSpend, nextIncome };
};

/** Just the safe-to-spend figure, without paying for a chart-length series. */
export const computeSafeToSpend = (args) => projectBalances({ ...args, days: 0 }).safeToSpend;

